import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { Button, MessagePlugin, Space } from 'tdesign-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { isEmpty } from 'lodash';
import { useMemoizedFn } from 'ahooks';
import { runFlow } from '@/services/execFlowService';
import { fetchFlowInitVariables } from '@/slices/execFlowSlice';
import OperateTitle from '@/pages/Maintenance/Executive/components/OperateTitle';
import { validateAll } from '@/pages/Maintenance/Executive/components/Variables';
import { isVoid } from '@/utils/common';
import Advance from './Advance';
import ConfigVariables from './ConfigVariables';
import Distribute from './Distribute';

function FlowSteps({
  fetchFlowInitVariables,
  envId,
  hostsList,
  cmpVariables,
  ipVariables,
  versionList,
  currentCluster,
  clusterId,
  steps,
  fileInfoList,
}) {
  const [current, setCurrent] = useState(0);
  const [searchParams] = useSearchParams();
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  const flowId = Number(searchParams.get('flowId'));
  const flowType = Number(searchParams.get('flowType'));

  const getVariables = useMemoizedFn(() => {
    setCurrent(1);
    const paramHosts = [];
    hostsList.forEach((item) => {
      const { stepId, hosts } = item;
      hosts.forEach((host) => {
        paramHosts.push({
          stepId,
          hostsId: host.id,
        });
      });
    });

    fetchFlowInitVariables({
      clusterId,
      envId,
      flowId,
      hostsList: flowType === 1 ? [] : paramHosts, // K8s 无主机
    });
  });

  useEffect(() => {
    // K8s无需主机选择
    if (flowType === 1) {
      getVariables();
    }
    if (flowType === 0) {
      setCurrent(0);
    }
  }, [flowType, getVariables]);

  const doNext = () => {
    // 先检查主机是否选择完毕
    let existsEmpty = false;
    steps.forEach((step) => {
      const currentHosts = hostsList.find((host) => host.stepId === step.stepId)?.hosts;
      if (isEmpty(currentHosts)) {
        existsEmpty = true;
      }
    });
    if (existsEmpty) {
      MessagePlugin.warning('请先完成主机选择');
      return;
    }
    getVariables();
  };

  const doSubmit = async () => {
    let fileNotFinish = false;
    fileInfoList.forEach((item) => {
      if (isVoid(item.fileName)) {
        fileNotFinish = true;
      }
    });
    if (fileNotFinish) {
      MessagePlugin.warning('请先完成所有文件上传');
      return;
    }
    const runParams = {
      clusterId,
      envId,
      flowStencilId: flowId,
      hostIdK8s: '',
      advancedOptionsVersionList: versionList.map((item) => ({
        componentStencilId: item.componentsId,
        productComponentId: item.id,
      })),
      hostsList: hostsList.map((item) => ({
        stepId: item.stepId,
        hostsId: item.hosts.map((host) => host.id).join(','),
      })),
      ipVariableList: ipVariables.map((item) => ({
        currenValue: item.defaultValue,
        ip: item.hostId,
        key: item.key,
        scriptStencilVariableId: item.variableId,
        stencilId: item.scriptId,
        stencilType: item.stencilType,
      })),
      variableList: cmpVariables.map((item) => ({
        currenValue: item.defaultValue,
        key: item.key,
        scriptStencilVariableId: item.variableId,
        stencilId: item.scriptId,
        stencilType: item.stencilType,
      })),
      // fileInfoList,
    };

    // K8s
    if (flowType === 1) {
      runParams.hostIdK8s = currentCluster.hostIdK8s;
      runParams.hostsList = [];
      runParams.ipVariableList = [];
    }
    try {
      setSubmitting(true);

      const {
        data: { id, clusterId },
      } = await runFlow(runParams);
      MessagePlugin.success('执行成功');
      navigate(`/maintenance/executive/record/detail?id=${id}&clusterId=${clusterId}`);
    } catch (error) {
      console.trace(error);
      throw error;
    } finally {
      setSubmitting(false);
    }
  };

  const goAdvance = () => {
    if (validateAll(cmpVariables, ipVariables)) {
      setCurrent(2);
    }
  };

  return (
    <Root className='fadeIn'>
      <OperateTitle title='配置信息' />
      {current === 0 && (
        <>
          <Distribute />
          <Footer>
            <Button theme='primary' onClick={doNext}>
              下一步：填写变量
            </Button>
          </Footer>
        </>
      )}
      {current === 1 && (
        <>
          <ConfigVariables />
          <Footer>
            <Space>
              {flowType === 0 && (
                <Button theme='default' variant='outline' onClick={() => setCurrent(0)}>
                  上一步：执行清单
                </Button>
              )}
              <Button theme='primary' onClick={goAdvance}>
                下一步：高级选项
              </Button>
            </Space>
          </Footer>
        </>
      )}
      {current === 2 && (
        <>
          <Advance />
          <Footer>
            <Space>
              <Button theme='default' variant='outline' onClick={() => setCurrent(1)}>
                上一步：填写变量
              </Button>
              <Button theme='primary' loading={submitting} onClick={doSubmit}>
                提交执行
              </Button>
            </Space>
          </Footer>
        </>
      )}
    </Root>
  );
}

export default connect(
  (state) => ({
    clusterId: state.execFlow.currentCluster?.id,
    envId: state.execFlow.currentEnv?.id,
    hostsList: state.execFlow.hostsList,
    fileInfoList: state.execFlow.fileInfoList,
    versionList: state.execFlow.versionList,
    steps: state.execFlow.steps,
    currentCluster: state.execFlow.currentCluster,
    ipVariables: state.execFlow.ipVariables,
    cmpVariables: state.execFlow.cmpVariables,
  }),
  {
    fetchFlowInitVariables,
  },
)(FlowSteps);

const Root = styled.div`
  border-radius: 8px;
  border: 1px solid #e1e1e1;
  margin-bottom: 16px;
`;

const Footer = styled.div`
  display: flex;
  border-top: 1px solid #e1e1e1;
  padding: 10px 20px;
  justify-content: flex-end;
`;
